import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { RouteLoadError, retryFailedRoutes } from '@/lib/lazy-route';
import { hasWorkBeforeUnload } from '@/stores/workDrafts';
import { QueryErrorState } from '@/v2/design-system/primitives';

interface BoundaryProps {
  resetKey: string;
  children: ReactNode;
}

interface BoundaryState {
  error: unknown;
  /** A tela em que a falha apareceu. */
  caughtAt: string | null;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, caughtAt: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState) {
    if (state.error && state.caughtAt === null) return { caughtAt: props.resetKey };
    if (!state.error && state.caughtAt !== null) return { caughtAt: null };
    return null;
  }

  componentDidUpdate() {
    // Sair da tela que falhou (menu, voltar do navegador) desfaz o aviso e deixa tentar de novo.
    if (this.state.error && this.state.caughtAt !== null && this.state.caughtAt !== this.props.resetKey) this.retry();
  }

  retry = () => {
    retryFailedRoutes();
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // Sem texto guardado vale o de sempre: a página recarrega sozinha (lib/chunk-reload).
    if (!(error instanceof RouteLoadError) || !hasWorkBeforeUnload()) throw error;
    return (
      <QueryErrorState
        data-testid="route-load-failed"
        title="Não foi possível carregar esta tela."
        description="Seu texto continua guardado."
        retryLabel="Tentar de novo"
        onRetry={this.retry}
      />
    );
  }
}

/**
 * Story 65.45 — uma tela que não carrega, com texto digitado e ainda não salvo, não recarrega a
 * página: o aviso fica no lugar da tela, com o menu à volta, e o texto continua na memória.
 */
export function RouteLoadBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  // A chave do roteador pode repetir (entrada inicial, histórico mexido por fora): o caminho vai junto.
  return <Boundary resetKey={`${location.key}|${location.pathname}${location.search}`}>{children}</Boundary>;
}
